export interface DosingInfo {
  weightRange: string;
  loadingDose: number;
  maintenanceDose: number;
  numberOfUltomirisVials300Loading: number;
  numberOfUltomirisVials300Maintenances: number;
  minimumInfusionTimeMinLoading: number;
  minimumInfusionTimeMinMaintenances: number;
  dosingIntervalByWeeks: number;
}

export interface RegimenData {
  dose: {
    [weightKey: string]: DosingInfo;
  };
}

export interface RegionalDosingData {
  [region: string]: RegimenData;
}

// Default dosing data (for all countries except KSA)
export const defaultDosingData: RegimenData = {
  dose: {
    "10-20": {
      weightRange: "≥10 to <20",
      loadingDose: 600,
      maintenanceDose: 600,
      numberOfUltomirisVials300Loading: 2,
      numberOfUltomirisVials300Maintenances: 2,
      minimumInfusionTimeMinLoading: 113,
      minimumInfusionTimeMinMaintenances: 113,
      dosingIntervalByWeeks: 4,
    },
    "20-30": {
      weightRange: "≥20 to <30",
      loadingDose: 900,
      maintenanceDose: 2100,
      numberOfUltomirisVials300Loading: 3,
      numberOfUltomirisVials300Maintenances: 7,
      minimumInfusionTimeMinLoading: 86,
      minimumInfusionTimeMinMaintenances: 194,
      dosingIntervalByWeeks: 8,
    },
    "30-40": {
      weightRange: "≥30 to <40",
      loadingDose: 1200,
      maintenanceDose: 2700,
      numberOfUltomirisVials300Loading: 4,
      numberOfUltomirisVials300Maintenances: 9,
      minimumInfusionTimeMinLoading: 77,
      minimumInfusionTimeMinMaintenances: 167,
      dosingIntervalByWeeks: 8,
    },
    "40-60": {
      weightRange: "≥40 to <60",
      loadingDose: 2400,
      maintenanceDose: 3000,
      numberOfUltomirisVials300Loading: 8,
      numberOfUltomirisVials300Maintenances: 10,
      minimumInfusionTimeMinLoading: 114,
      minimumInfusionTimeMinMaintenances: 140,
      dosingIntervalByWeeks: 8,
    },
    "60-100": {
      weightRange: "≥60 to <100",
      loadingDose: 2700,
      maintenanceDose: 3300,
      numberOfUltomirisVials300Loading: 9,
      numberOfUltomirisVials300Maintenances: 11,
      minimumInfusionTimeMinLoading: 102,
      minimumInfusionTimeMinMaintenances: 120,
      dosingIntervalByWeeks: 8,
    },
    "100+": {
      weightRange: "≥100",
      loadingDose: 3000,
      maintenanceDose: 3600,
      numberOfUltomirisVials300Loading: 10,
      numberOfUltomirisVials300Maintenances: 12,
      minimumInfusionTimeMinLoading: 108,
      minimumInfusionTimeMinMaintenances: 132,
      dosingIntervalByWeeks: 8,
    },
  },
};

// KSA-specific dosing data
export const ksaDosingData: RegimenData = {
  dose: {
    "5-10": {
      weightRange: "≥ 5 to < 10",
      loadingDose: 600,
      maintenanceDose: 300,
      numberOfUltomirisVials300Loading: 2,
      numberOfUltomirisVials300Maintenances: 1,
      minimumInfusionTimeMinLoading: 228,
      minimumInfusionTimeMinMaintenances: 113,
      dosingIntervalByWeeks: 4,
    },
    "10-20": {
      weightRange: "≥10 to <20",
      loadingDose: 600,
      maintenanceDose: 600,
      numberOfUltomirisVials300Loading: 2,
      numberOfUltomirisVials300Maintenances: 2,
      minimumInfusionTimeMinLoading: 113,
      minimumInfusionTimeMinMaintenances: 113,
      dosingIntervalByWeeks: 4,
    },
    "20-30": {
      weightRange: "≥20 to <30",
      loadingDose: 900,
      maintenanceDose: 2100,
      numberOfUltomirisVials300Loading: 3,
      numberOfUltomirisVials300Maintenances: 7,
      minimumInfusionTimeMinLoading: 86,
      minimumInfusionTimeMinMaintenances: 194,
      dosingIntervalByWeeks: 8,
    },
    "30-40": {
      weightRange: "≥30 to <40",
      loadingDose: 1200,
      maintenanceDose: 2700,
      numberOfUltomirisVials300Loading: 4,
      numberOfUltomirisVials300Maintenances: 9,
      minimumInfusionTimeMinLoading: 77,
      minimumInfusionTimeMinMaintenances: 167,
      dosingIntervalByWeeks: 8,
    },
    "40-60": {
      weightRange: "≥40 to <60",
      loadingDose: 2400,
      maintenanceDose: 3000,
      numberOfUltomirisVials300Loading: 8,
      numberOfUltomirisVials300Maintenances: 10,
      minimumInfusionTimeMinLoading: 114,
      minimumInfusionTimeMinMaintenances: 140,
      dosingIntervalByWeeks: 8,
    },
    "60-100": {
      weightRange: "≥60 to <100",
      loadingDose: 2700,
      maintenanceDose: 3300,
      numberOfUltomirisVials300Loading: 9,
      numberOfUltomirisVials300Maintenances: 11,
      minimumInfusionTimeMinLoading: 102,
      minimumInfusionTimeMinMaintenances: 120,
      dosingIntervalByWeeks: 8,
    },
    "100+": {
      weightRange: "≥100",
      loadingDose: 3000,
      maintenanceDose: 3600,
      numberOfUltomirisVials300Loading: 10,
      numberOfUltomirisVials300Maintenances: 12,
      minimumInfusionTimeMinLoading: 108,
      minimumInfusionTimeMinMaintenances: 132,
      dosingIntervalByWeeks: 8,
    },
  },
};

// Combined regional dosing data
export const regionalDosingData: RegionalDosingData = {
  default: defaultDosingData,
  ksa: ksaDosingData,
};

// Helper function to get dosing info based on region, regimen type and weight
export const getDosingInfo = (
  region: string,
  weightKey: string
): DosingInfo | null => {
  const regionKey = region.toLowerCase() === "ksa" ? "ksa" : "default";
  const regionData = regionalDosingData[regionKey];
  return regionData.dose[weightKey] || null;
};

export const baseCategories = [
  { label: "≥ 10 to < 20 kg", value: "10-20", selected: true },
  { label: "≥20 to <30 kg", value: "20-30" },
  { label: "≥30 to <40 kg", value: "30-40" },
  { label: "≥40 to <60 kg", value: "40-60" },
  { label: "≥60 to <100 kg", value: "60-100" },
  { label: "≥100 kg", value: "100+" },
];
